/* "Everything Connected. Everything Under Control." — the master Cornerstone Ops commercial (#4).

   CHAOS -> CONNECTION -> INTELLIGENCE -> CONTROL, in roughly 90 seconds. The software interface is
   the hero: every product frame is a real screenshot captured from a synthetic demo organization
   (scripts/capture_ad_shots.py), never the operator's own business.

   SYNC RULE (the fix for "the words and the pop-ups are off"): nothing on screen is timed to a
   fraction of its scene any more. Every headline, card, ring and number is anchored to the
   absolute timestamp of the narration cue it belongs to, read from ad4/script.json via on()/at().
   So "One for scheduling." lands the scheduling tile, and "Every owner." lands the owner screen.

   Motion rules, learned the hard way across the three earlier films and still in force:
     · frames, borders and all type are pinned to whole pixels and only cross-fade — they never
       move, so nothing can crawl or shimmer
     · anything that MOVES moves by GPU transform on a layer holding no text and no borders
     · no quantised/stepped motion anywhere; that is what made the last film visibly hop
   No fake testimonials, customer counts, awards, logos or statistics appear anywhere. */
import { BrandBadge } from "@/components/BrandMark";
import {
  Bell, Mail, MessageSquare, Table2, Calendar, CloudSun, Home, Wrench, Users, ClipboardCheck,
  Receipt, FileText, Zap, ArrowRight, Building2, BarChart3, Globe, Landmark, Brain,
} from "lucide-react";

const NAVY = "#070c1e";
const GOLD = "#d4af37";
const BLUE = "#8fb4ff";
const SHOT = (n) => `/ad3/shots/${n}.jpg`;

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const seg = (v, a, b) => {
  const x = clamp((v - a) / Math.max(b - a, 0.0001));
  return x * x * (3 - 2 * x);
};
const px = (n) => Math.round(n);

const Kicker = ({ children, u, o = 1, color = GOLD }) => (
  <p style={{ color, opacity: o, fontSize: `${1.55 * u}px`, fontWeight: 700,
              letterSpacing: `${0.5 * u}px`, textTransform: "uppercase" }}>{children}</p>
);

const Line = ({ children, u, o, color = "#fff", size = 4.3, tall }) => (
  <h2 className="font-display" style={{ color, fontWeight: 800, opacity: o,
    fontSize: `${(tall ? size * 0.95 : size) * u}px`, lineHeight: 1.06 }}>{children}</h2>
);

/* Two pinned lines under the hero frame. Neither can ever shift the other. */
const Punch = ({ u, lead, lead2, o, o2, tall }) => (
  <div style={{ position: "absolute", left: 0, right: 0, bottom: `${(tall ? 9 : 6.5) * u}px`,
                padding: "0 7%" }}>
    <Line u={u} o={o} tall={tall}>{lead}</Line>
    <Line u={u} o={o2} color={GOLD} tall={tall}>{lead2}</Line>
  </div>
);

/* A real product screen. The frame never moves; only the inner image layer is transformed. */
const Plate = ({ shot, p, u, w, h, tall, ar = 1.6, pos = "50% 0%", mark = null, zoomTo = 0.034 }) => {
  const inFrame = seg(p, 0, 0.1);
  const boxW = px(w * (tall ? 0.98 : 0.88));
  const top0 = tall ? h * 0.17 : h * 0.125;
  const regionH = tall ? h * 0.58 : h * 0.63;
  let pw = boxW;
  let ph = px(boxW / ar);
  if (ph > regionH) { ph = px(regionH); pw = px(regionH * ar); }
  const left = px((w - pw) / 2);
  const top = px(top0 + (regionH - ph) / 2);
  const zoom = (1.012 + p * zoomTo).toFixed(4);
  return (
    <div style={{ position: "absolute", left: `${left}px`, top: `${top}px`, width: `${pw}px`,
      height: `${ph}px`, opacity: inFrame, borderRadius: `${0.9 * u}px`, overflow: "hidden",
      border: "1px solid rgba(212,175,55,0.45)", boxShadow: "0 30px 90px rgba(0,0,0,0.65)",
      backgroundColor: "#0b1230" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${SHOT(shot)})`,
        backgroundSize: "cover", backgroundPosition: pos, backgroundRepeat: "no-repeat",
        transform: `scale(${zoom})`, transformOrigin: "50% 40%", willChange: "transform" }} />
      {mark && !tall && (
        <div style={{ position: "absolute", left: `${px(pw * mark[0])}px`,
          top: `${px(ph * mark[1])}px`, width: `${px(pw * mark[2])}px`,
          height: `${px(ph * mark[3])}px`, border: `${Math.max(2, px(u * 0.2))}px solid ${GOLD}`,
          borderRadius: `${0.5 * u}px`, opacity: seg(p, 0.32, 0.5),
          boxShadow: `0 0 ${2 * u}px rgba(212,175,55,0.5)` }} />
      )}
    </div>
  );
};

const Stage = ({ children, u, tall, kicker, p, glow = "50% 8%" }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
    <div className="absolute inset-0" style={{ background:
      `radial-gradient(95% 72% at ${glow}, rgba(30,52,124,0.72), rgba(5,9,22,1) 72%)` }} />
    {kicker && (
      <div style={{ position: "absolute", left: 0, right: 0, top: `${(tall ? 9 : 5.5) * u}px`,
                    padding: "0 7%" }}>
        <Kicker u={u} o={seg(p, 0, 0.06)}>{kicker}</Kicker>
      </div>
    )}
    {children}
  </div>
);

/* A product screen whose two lines land on two named narration cues. */
const ProductCut = (a) => (
  <Stage u={a.u} tall={a.tall} kicker={a.kicker} p={a.p}>
    <Plate {...a} />
    <Punch u={a.u} tall={a.tall} lead={a.lead} lead2={a.lead2}
           o={a.on(a.leadCue, -0.1)} o2={a.on(a.lead2Cue, a.lead2Off ?? -0.1)} />
  </Stage>
);

/* ---- 1. CHAOS · the notifications multiply ---------------------------------------------------- */
const PINGS = [
  [Bell, "Work order overdue", "Dune Ridge 4"],
  [MessageSquare, "Crew: running late", "2 unread"],
  [Mail, "Owner: any update?", "Inbox"],
  [CloudSun, "Wind advisory issued", "Coastal zone"],
  [Table2, "Turnover sheet edited", "v7_FINAL.xlsx"],
  [Calendar, "Double-booked crew", "Saturday"],
  [Bell, "Guest reported an issue", "Sound View 7"],
  [Mail, "Invoice question", "Owner thread"],
  [MessageSquare, "Vendor: need access code", "1 unread"],
  [Bell, "Inspection not submitted", "Yesterday"],
];

/* The pings pile up across the first spoken line and stop when the second line lands. */
const Chaos = ({ t, u, w, h, tall, on, at }) => {
  const cards = PINGS.slice(0, tall ? 8 : 10);
  const from = at("comms") + 0.1;
  const until = at("coord") - 0.25;
  const step = Math.max((until - from) / cards.length, 0.1);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: "#02040b" }}>
      <div className="absolute inset-0" style={{ opacity: on("comms", 0, 1.6) * 0.5, background:
        "radial-gradient(85% 60% at 50% 30%, rgba(36,58,132,0.5), rgba(2,4,11,1) 72%)" }} />
      {cards.map(([Icon, title, body], i) => {
        const born = from + i * step;
        const alive = seg(t, born, born + 0.22);
        const cols = tall ? 1 : 3;
        const col = i % cols;
        const row = Math.floor(i / cols);
        const cardW = tall ? w * 0.8 : w * 0.28;
        const x = tall ? w * 0.1 : w * 0.05 + col * (w * 0.315);
        const y = (tall ? h * 0.1 : h * 0.1) + row * (tall ? h * 0.085 : h * 0.135);
        return (
          <div key={`${title}-${i}`} style={{ position: "absolute", left: `${px(x)}px`,
            top: `${px(y)}px`, width: `${px(cardW)}px`, opacity: alive * 0.96,
            background: "rgba(255,255,255,0.96)", borderRadius: `${0.9 * u}px`,
            padding: `${0.85 * u}px ${1.1 * u}px`, boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: `${0.6 * u}px` }}>
              <Icon style={{ width: 1.25 * u, height: 1.25 * u, color: "#1b2a63", flexShrink: 0 }} />
              <span style={{ color: "#101a3d", fontWeight: 800, fontSize: `${1.2 * u}px`,
                             whiteSpace: "nowrap", overflow: "hidden" }}>{title}</span>
            </div>
            <p style={{ marginTop: `${0.25 * u}px`, color: "#55607f", fontWeight: 600,
                        fontSize: `${1.02 * u}px` }}>{body}</p>
          </div>
        );
      })}
      <div className="absolute inset-x-0" style={{ bottom: 0, padding: `0 7% ${5 * u}px`, background:
        "linear-gradient(to top, rgba(2,4,11,0.98) 18%, rgba(2,4,11,0.7) 70%, transparent)" }}>
        <Line u={u} tall={tall} size={4.5} o={on("comms", -0.1)}>
          Your operation doesn't have a<br />communication problem.
        </Line>
        <Line u={u} tall={tall} size={4.5} color={GOLD} o={on("coord", -0.1)}>
          It has a coordination problem.
        </Line>
      </div>
    </div>
  );
};

/* ---- 2. THE QUESTION · one system for everything ---------------------------------------------- */
/* Each tile is bound to the line that names it, so "One for vendors." lands the vendor tile. */
const SILOS = [
  [Calendar, "Scheduling", "sched"], [ClipboardCheck, "Inspections", "insp"],
  [Wrench, "Vendors", "vend"], [Users, "Owners", "own"],
  [CloudSun, "Weather", "weath"], [Landmark, "Accounting", "acct"],
];

const Systems = ({ p, u, w, h, tall, on }) => (
  <Stage u={u} tall={tall} p={p} kicker="One operation. Six systems.">
    <div style={{ position: "absolute", left: 0, right: 0, top: `${(tall ? 18 : 15) * u}px`,
                  padding: "0 7%" }}>
      <Line u={u} tall={tall} size={4.4} o={on("howmany", -0.15)}>
        How many systems does it take<br />
        <span style={{ color: GOLD }}>to run one operation?</span>
      </Line>
    </div>
    {SILOS.map(([Icon, label, cue], i) => {
      const live = on(cue, -0.12, 0.2);
      const cols = tall ? 2 : 3;
      const cw = (tall ? w * 0.42 : w * 0.27);
      const x = (tall ? w * 0.06 : w * 0.07) + (i % cols) * (cw + w * 0.025);
      const y = (tall ? h * 0.46 : h * 0.42) + Math.floor(i / cols) * (tall ? h * 0.12 : h * 0.17);
      return (
        <div key={label} style={{ position: "absolute", left: `${px(x)}px`, top: `${px(y)}px`,
          width: `${px(cw)}px`, opacity: live, background: "rgba(10,16,40,0.92)",
          border: `1px solid rgba(143,180,255,0.24)`, borderRadius: `${0.9 * u}px`,
          padding: `${1.1 * u}px` }}>
          <Icon style={{ width: 2 * u, height: 2 * u, color: BLUE }} />
          <p style={{ marginTop: `${0.6 * u}px`, color: "#fff", fontWeight: 700,
                      fontSize: `${1.45 * u}px`, whiteSpace: "nowrap" }}>{label}</p>
        </div>
      );
    })}
    <div style={{ position: "absolute", left: 0, right: 0, bottom: `${(tall ? 8 : 6) * u}px`,
                  padding: "0 7%" }}>
      <Line u={u} tall={tall} size={4.1} color={GOLD} o={on("holding", 0.9)}>
        And you're still the one<br />holding it all together.
      </Line>
    </div>
  </Stage>
);

/* ---- 3. THE TURN · everything stops, one interface forms -------------------------------------- */
const Forming = ({ p, u, w, h, on }) => {
  const build = seg(p, 0.08, 0.6);
  const ringR = (0.16 + build * 0.1) * Math.min(w, h);
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden"
         style={{ background: "#02040b" }}>
      <div className="absolute inset-0" style={{ opacity: build, background:
        "radial-gradient(60% 50% at 50% 50%, rgba(40,70,160,0.45), rgba(2,4,11,1) 72%)" }} />
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ position: "absolute", width: `${px(ringR * (1 + i * 0.5))}px`,
          height: `${px(ringR * (1 + i * 0.5))}px`, borderRadius: "50%",
          border: `1px solid rgba(212,175,55,${0.4 - i * 0.11})`,
          opacity: seg(p, 0.05 + i * 0.08, 0.3 + i * 0.08) }} />
      ))}
      <div style={{ position: "relative", textAlign: "center" }}>
        <div style={{ width: `${7 * u}px`, height: `${7 * u}px`, margin: "0 auto",
                      opacity: on("whatif", -0.35, 0.5) }}>
          <BrandBadge size="h-full w-full" icon="h-3/5 w-3/5" />
        </div>
        <h2 className="font-display" style={{ marginTop: `${2.4 * u}px`, color: "#fff",
          fontWeight: 800, fontSize: `${5.2 * u}px`, lineHeight: 1.06,
          opacity: on("whatif", -0.12) }}>
          What if you<br /><span style={{ color: GOLD }}>didn't have to?</span>
        </h2>
      </div>
    </div>
  );
};

/* ---- 4. THE REVEAL --------------------------------------------------------------------------- */
const Reveal = (a) => (
  <ProductCut {...a} shot="dashboard" ar={1.6} kicker="The operational command center"
    lead="Meet Cornerstone Ops." lead2="Your entire operation, together."
    leadCue="meet" lead2Cue="built" zoomTo={0.05} />
);

/* ---- 5. THE ECOSYSTEM ------------------------------------------------------------------------ */
/* "Every / <word>." lands exactly on its own line, over the screen that actually proves it. */
const Every = ({ word, cue, ...a }) => (
  <Stage u={a.u} tall={a.tall} kicker={a.kicker} p={a.p}>
    <Plate {...a} />
    <div style={{ position: "absolute", left: 0, right: 0, bottom: `${(a.tall ? 9 : 6.5) * a.u}px`,
                  padding: "0 7%" }}>
      <Line u={a.u} tall={a.tall} size={6.2} o={a.on(cue, -0.12, 0.22)}>Every</Line>
      <Line u={a.u} tall={a.tall} size={6.2} color={GOLD}
            o={a.on(cue, 0.34, 0.22)}>{word}.</Line>
    </div>
  </Stage>
);

const PropertiesCut = (a) => (
  <Every {...a} word="property" cue="everyprop" shot="properties" ar={1.6}
    kicker="Portfolio · every property you run" />
);
const WorkforceCut = (a) => (
  <Every {...a} word="team" cue="everyteam" shot="team" ar={1.6}
    kicker="Your people · roles and access" />
);
const VendorsCut = (a) => (
  <Every {...a} word="vendor" cue="everyvendor" shot="vendors" ar={1.6}
    kicker="Vendors · dispatch, service, approval" />
);
const OwnersCut = (a) => (
  <Every {...a} word="owner" cue="everyowner" shot="reports" ar={1.6}
    kicker="Owner reporting · proof of work" />
);

/* ---- 6. WEATHER INTELLIGENCE · the major visual ----------------------------------------------- */
const StormMap = ({ p, u, w, h, tall, t, on, at }) => {
  const boxW = px(w * (tall ? 0.98 : 0.88));
  const ph = Math.min(px(boxW / 1.6), px((tall ? h * 0.58 : h * 0.63)));
  const pw = ph === px(boxW / 1.6) ? boxW : px(ph * 1.6);
  const left = px((w - pw) / 2);
  const top = px((tall ? h * 0.17 : h * 0.125) + ((tall ? h * 0.58 : h * 0.63) - ph) / 2);
  // the rings land on "...are in its path", i.e. the back half of the wxpath line
  const ringFrom = at("wxpath") + 1.45;
  return (
    <Stage u={u} tall={tall} p={p} kicker="Weather intelligence · NWS + NHC">
      <div style={{ position: "absolute", left: `${left}px`, top: `${top}px`, width: `${pw}px`,
        height: `${ph}px`, opacity: seg(p, 0, 0.1), borderRadius: `${0.9 * u}px`,
        overflow: "hidden", border: "1px solid rgba(212,175,55,0.45)",
        boxShadow: "0 30px 90px rgba(0,0,0,0.65)", backgroundColor: "#0b1230" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${SHOT("map_dark")})`,
          backgroundSize: "cover", backgroundPosition: "50% 0%",
          transform: `scale(${(1.012 + p * 0.05).toFixed(4)})`, transformOrigin: "62% 42%",
          willChange: "transform" }} />
        {[[0.684, 0.432], [0.707, 0.55], [0.72, 0.596], [0.735, 0.644]].map(([fx, fy], i) => (
          <div key={i} style={{ position: "absolute", left: `${px(pw * fx)}px`,
            top: `${px(ph * fy)}px`, width: `${px(3.4 * u)}px`, height: `${px(3.4 * u)}px`,
            marginLeft: `${px(-1.7 * u)}px`, marginTop: `${px(-1.7 * u)}px`, borderRadius: "50%",
            border: `${Math.max(2, px(u * 0.2))}px solid ${GOLD}`,
            opacity: seg(t, ringFrom + i * 0.14, ringFrom + 0.3 + i * 0.14),
            boxShadow: `0 0 ${1.6 * u}px rgba(212,175,55,0.6)` }} />
        ))}
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: `${(tall ? 9 : 6.5) * u}px`,
                    padding: "0 7%" }}>
        <Line u={u} tall={tall} size={4.3} o={on("wxchange", -0.12)}>
          And when the weather changes,
        </Line>
        <Line u={u} tall={tall} size={4.3} color={GOLD} o={on("wxpath", -0.12)}>
          Cornerstone knows which properties<br />are in its path.
        </Line>
      </div>
    </Stage>
  );
};

const BriefingCut = (a) => (
  <ProductCut {...a} shot="briefing" ar={0.78} kicker="AI operational briefing"
    lead="Because weather intelligence" lead2="should be operational intelligence."
    leadCue="wxops" lead2Cue="wxops" lead2Off={1.65} />
);

/* ---- 7. CONNECTED SYSTEMS · reservation flows all the way through ----------------------------- */
const FLOW = [
  [Globe, "Reservation"], [Wrench, "Work order"], [Users, "Team assigned"],
  [ClipboardCheck, "Inspection"], [FileText, "Owner report"],
];

/* The chain builds while the first line is spoken; both lines land on their own cue. */
const Chain = ({ p, u, w, h, tall, t, on, at, kicker, items, lead, lead2, leadCue, lead2Cue }) => {
  const from = at(leadCue) + 0.1;
  const step = Math.max((at(lead2Cue) - 0.25 - from) / items.length, 0.14);
  return (
    <Stage u={u} tall={tall} p={p} kicker={kicker}>
      {items.map(([Icon, label], i) => {
        const born = from + i * step;
        const live = seg(t, born, born + 0.24);
        const n = items.length;
        const cw = tall ? w * 0.76 : (w * 0.84) / n - w * 0.012;
        const x = tall ? w * 0.12 : w * 0.08 + i * ((w * 0.84) / n);
        const y = tall ? h * 0.24 + i * (h * 0.082) : h * 0.33;
        return (
          <div key={label}>
            <div style={{ position: "absolute", left: `${px(x)}px`, top: `${px(y)}px`,
              width: `${px(cw)}px`, opacity: live, background: "rgba(10,16,40,0.94)",
              border: `1px solid ${live > 0.9 ? "rgba(212,175,55,0.5)" : "rgba(143,180,255,0.24)"}`,
              borderRadius: `${0.9 * u}px`, padding: `${1 * u}px` }}>
              <Icon style={{ width: 1.9 * u, height: 1.9 * u, color: GOLD }} />
              <p style={{ marginTop: `${0.5 * u}px`, color: "#fff", fontWeight: 700,
                          fontSize: `${1.3 * u}px` }}>{label}</p>
            </div>
            {i < n - 1 && (
              <ArrowRight style={{ position: "absolute",
                left: tall ? `${px(w * 0.12 + cw / 2 - u)}px` : `${px(x + cw + w * 0.004)}px`,
                top: tall ? `${px(y + h * 0.064)}px` : `${px(y + 2.2 * u)}px`,
                width: 1.7 * u, height: 1.7 * u, color: GOLD,
                opacity: seg(t, born + 0.12, born + 0.34),
                transform: tall ? "rotate(90deg)" : "none" }} />
            )}
          </div>
        );
      })}
      <Punch u={u} tall={tall} lead={lead} lead2={lead2}
             o={on(leadCue, -0.12)} o2={on(lead2Cue, -0.12)} />
    </Stage>
  );
};

const Connected = (a) => (
  <Chain {...a} kicker="Airbnb &amp; VRBO connectivity · automations" items={FLOW}
    leadCue="connect" lead2Cue="autom"
    lead="Connect the systems you already use." lead2="Automate the rest." />
);

/* ---- 8. FINANCIAL OPERATIONS · money stays in the accounting system -------------------------- */
const MONEY = [[Users, "Owner"], [Receipt, "Payment provider"], [Landmark, "Accounting"],
                [Building2, "Cornerstone"]];

const Finance = (a) => (
  <Chain {...a} kicker="Financial workflows" items={MONEY}
    leadCue="fin" lead2Cue="acctctrl"
    lead="Bring financial workflows into the operation."
    lead2="Your accounting system keeps the money." />
);

/* ---- 9. THE COMMAND CENTER MOMENT · everything converges -------------------------------------- */
const CONVERGE = [
  [Home, "Properties"], [Users, "Teams"], [Wrench, "Work orders"], [ClipboardCheck, "Inspections"],
  [Building2, "Vendors"], [FileText, "Owners"], [CloudSun, "Weather"], [Globe, "Reservations"],
  [BarChart3, "Reports"], [Receipt, "Financials"], [Zap, "Automations"], [Brain, "Intelligence"],
];

const Converge = ({ p, u, w, h, tall, t, on, at }) => {
  // everything draws into the badge while she says "...isn't about managing more software"
  const pull = seg(t, at("notsoftware") + 0.5, at("operation") - 0.1);
  const cx = w / 2;
  const cy = tall ? h * 0.42 : h * 0.44;
  const R = Math.min(w, h) * (tall ? 0.4 : 0.37);
  return (
    <Stage u={u} tall={tall} p={p} kicker="One operation" glow="50% 44%">
      {CONVERGE.map(([Icon, label], i) => {
        const a = (i / CONVERGE.length) * Math.PI * 2 - Math.PI / 2;
        const r = R * (1 - pull * 0.82);
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r * (tall ? 1.15 : 0.62);
        return (
          <div key={label} style={{ position: "absolute", left: `${px(x)}px`, top: `${px(y)}px`,
            transform: "translate(-50%, -50%)", opacity: (1 - pull * 0.75) * seg(p, 0.02, 0.1),
            display: "flex", alignItems: "center", gap: `${0.45 * u}px`,
            background: "rgba(10,16,40,0.9)", border: "1px solid rgba(143,180,255,0.22)",
            borderRadius: `${5 * u}px`, padding: `${0.5 * u}px ${0.9 * u}px`, whiteSpace: "nowrap" }}>
            <Icon style={{ width: 1.1 * u, height: 1.1 * u, color: BLUE }} />
            <span style={{ color: "#dce6ff", fontWeight: 700, fontSize: `${1.05 * u}px` }}>{label}</span>
          </div>
        );
      })}
      <div style={{ position: "absolute", left: `${px(cx)}px`, top: `${px(cy)}px`,
        transform: "translate(-50%, -50%)", width: `${px(9 * u)}px`, height: `${px(9 * u)}px`,
        opacity: on("notsoftware", 1.6, 0.9) }}>
        <BrandBadge size="h-full w-full" icon="h-3/5 w-3/5" />
      </div>
      <Punch u={u} tall={tall}
        lead="Running a property operation isn't about managing more software."
        lead2="It's about managing the operation."
        o={on("notsoftware", -0.12)} o2={on("operation", -0.12)} />
    </Stage>
  );
};

/* ---- 10. INTELLIGENCE ------------------------------------------------------------------------ */
const Intel = (a) => (
  <ProductCut {...a} shot="portfolio_ai" ar={1.92} kicker="Operational intelligence"
    lead="Turn operational data into decisions."
    lead2="Know what needs you — before it's a problem."
    leadCue="data" lead2Cue="attention" />
);

/* ---- 11. SCALE ------------------------------------------------------------------------------- */
/* The number changes on the word: five / twenty-five / one hundred / something bigger. */
const Scale = ({ p, u, tall, t, on, at }) => {
  const steps = [["five", 5, "5"], ["twentyfive", 25, "25"], ["hundred", 100, "100"],
                 ["bigger", 420, "Something bigger"]];
  const active = steps.filter(([cue]) => t >= at(cue) - 0.1).pop() || steps[0];
  const [, count, label] = active;
  const dots = Math.min(count, 420);
  return (
    <Stage u={u} tall={tall} p={p} kicker="Built to scale" glow="50% 50%">
      <div className="absolute inset-0 flex items-center justify-center" style={{ padding: "12% 8%" }}>
        {/* held to a block, not a single hairline row, so the count reads as a portfolio */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: `${0.5 * u}px`,
          justifyContent: "center", width: `${px(Math.ceil(Math.sqrt(dots)) * 1.66 * u)}px`,
          opacity: on("five", -0.2, 0.3) }}>
          {Array.from({ length: dots }).map((_, i) => (
            <span key={i} style={{ width: `${1.15 * u}px`, height: `${1.15 * u}px`,
              borderRadius: `${0.3 * u}px`,
              background: i % 9 === 0 ? GOLD : "rgba(143,180,255,0.5)",
              opacity: clamp((dots - i) / 40 + 0.3) }} />
          ))}
        </div>
      </div>
      <div className="absolute inset-x-0 text-center" style={{ top: `${(tall ? 15 : 11) * u}px`,
                                                               padding: "0 8%" }}>
        <p className="font-display" style={{ color: "#fff", fontWeight: 800,
          fontSize: `${(label.length > 4 ? 4.4 : 8) * u}px`, lineHeight: 1 }}>{label}</p>
        <Kicker u={u} o={0.85}>{label.length > 4 ? "Keep going" : "Properties"}</Kicker>
      </div>
      <div className="absolute inset-x-0 text-center" style={{ bottom: `${(tall ? 9 : 6.5) * u}px`,
                                                               padding: "0 8%" }}>
        <Line u={u} tall={tall} size={4} color={GOLD} o={on("bigger", 0.9)}>
          The interface never gets heavier.
        </Line>
      </div>
    </Stage>
  );
};

/* ---- 12. FINAL STATEMENT --------------------------------------------------------------------- */
const Final = ({ u, tall, on }) => {
  const a = on("stop", -0.12);
  const b = on("command", -0.12);
  const logo = on("brand", -0.3, 0.5);
  const url = on("brand", 0.9, 0.5);
  const verse = on("closing", 1.9, 0.7);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
         style={{ background: NAVY, padding: "8%" }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(74% 54% at 50% 42%, rgba(38,66,152,0.62), rgba(3,6,16,1) 74%)" }} />
      <div style={{ position: "relative" }}>
        <p className="font-display" style={{ color: "rgba(226,232,255,0.92)", fontWeight: 800,
          fontSize: `${4.4 * u}px`, lineHeight: 1.08, opacity: a }}>
          Stop chasing your operation.
        </p>
        <p className="font-display" style={{ color: GOLD, fontWeight: 800,
          fontSize: `${7 * u}px`, lineHeight: 1.04, opacity: b }}>
          Command it.
        </p>
        <div style={{ opacity: logo, marginTop: `${3 * u}px` }}>
          <div className="flex justify-center">
            <div style={{ width: `${9.5 * u}px`, height: `${9.5 * u}px` }}>
              <BrandBadge size="h-full w-full" icon="h-3/5 w-3/5" />
            </div>
          </div>
          <h2 className="font-display" style={{ marginTop: `${1.8 * u}px`, color: "#fff",
            fontWeight: 800, fontSize: `${6 * u}px`, lineHeight: 1 }}>Cornerstone Ops</h2>
          <div style={{ height: 1, width: `${24 * u}px`, margin: `${1.6 * u}px auto`,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
          <p style={{ color: GOLD, fontSize: `${1.9 * u}px`, fontWeight: 700,
            letterSpacing: `${0.5 * u}px`, textTransform: "uppercase" }}>
            The Operational Command Center
          </p>
        </div>
        {/* the one thing they must remember, held large */}
        <p className="font-display" style={{ marginTop: `${2.6 * u}px`, color: "#fff",
          fontWeight: 800, fontSize: `${(tall ? 4.2 : 4.8) * u}px`, letterSpacing: `${0.05 * u}px`,
          opacity: url }}>
          cornerstoneops.io
        </p>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: `${4.5 * u}px`,
                    opacity: verse, padding: "0 8%" }}>
        <div style={{ height: 1, width: `${15 * u}px`, margin: `0 auto ${1.1 * u}px`,
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        <p style={{ color: GOLD, fontSize: `${1.3 * u}px`, fontWeight: 700,
          letterSpacing: `${0.38 * u}px`, textTransform: "uppercase" }}>Psalm 24:1</p>
        <p style={{ marginTop: `${0.6 * u}px`, color: "rgba(216,228,255,0.85)",
          fontSize: `${1.75 * u}px`, fontStyle: "italic" }}>
          "The earth is the LORD'S, and everything in it."
        </p>
      </div>
    </div>
  );
};

/* Voice cue -> scene. Consecutive cues sharing a scene are merged by the page. */
export const MASTER_SCENE_OF = {
  comms: "chaos", coord: "chaos",
  howmany: "systems", sched: "systems", insp: "systems", vend: "systems", own: "systems",
  weath: "systems", acct: "systems", holding: "systems",
  whatif: "forming",
  meet: "reveal", built: "reveal",
  everyprop: "properties", everyteam: "workforce", everyvendor: "vendors", everyowner: "owners",
  wxchange: "stormmap", wxpath: "stormmap", wxops: "briefing",
  connect: "connected", autom: "connected",
  fin: "finance", acctctrl: "finance",
  notsoftware: "converge", operation: "converge",
  data: "intel", attention: "intel",
  five: "scale", twentyfive: "scale", hundred: "scale", bigger: "scale",
  stop: "final", command: "final", brand: "final", closing: "final",
};

const RENDERERS = {
  chaos: Chaos, systems: Systems, forming: Forming, reveal: Reveal, properties: PropertiesCut,
  workforce: WorkforceCut, vendors: VendorsCut, owners: OwnersCut, stormmap: StormMap,
  briefing: BriefingCut, connected: Connected, finance: Finance, converge: Converge,
  intel: Intel, scale: Scale, final: Final,
};

const PRELOAD = ["dashboard", "properties", "team", "vendors", "reports", "briefing",
                 "portfolio_ai", "map_dark"];

export default function MasterFilm({ t = 0, segments = [], cues = {}, w = 1920, h = 1080 }) {
  const u = Math.min(w, h) / 100;
  const tall = h / w > 1.25;
  const idx = segments.reduce((best, s, i) => (t >= s.start ? i : best), 0);
  const active = segments[idx];
  if (!active) return <div className="absolute inset-0" style={{ background: NAVY }} />;
  const Scene = RENDERERS[active.scene] || Chaos;
  const p = clamp((t - active.start) / Math.max(active.end - active.start, 0.001));
  // every reveal is anchored to the narration, never to the scene's own progress
  const at = (key) => (cues[key] ? cues[key].start : active.start);
  const on = (key, off = 0, dur = 0.3) => seg(t, at(key) + off, at(key) + off + dur);
  const into = Math.min(clamp((t - active.start) / 0.3),
                        1 - clamp((t - (active.end - 0.2)) / 0.2));
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}
         data-testid={`master-scene-${active.scene}`}>
      <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", opacity: 0.01 }}>
        {PRELOAD.map((n) => <img key={n} src={SHOT(n)} alt="" width="1" height="1" />)}
      </div>
      <div className="absolute inset-0" style={{ opacity: into }}>
        <Scene p={p} t={t} u={u} w={w} h={h} tall={tall} on={on} at={at} />
      </div>
      <div className="absolute inset-0" style={{ pointerEvents: "none", opacity: 0.4, background:
        `radial-gradient(56% 42% at ${(50 + 7 * Math.sin(t * 0.17)).toFixed(2)}% ${(46 + 5 * Math.cos(t * 0.14)).toFixed(2)}%,
         rgba(96,140,255,0.07), transparent 70%)` }} />
      <div className="absolute inset-0" style={{ opacity: 0.035, pointerEvents: "none",
        backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
        backgroundSize: `${0.55 * u}px ${0.55 * u}px` }} />
    </div>
  );
}
